import { surfaceV100Background } from "@Components/common/byb-ds/buttons/styled-components";
import { BybButtonVR } from "@Components/game/common";
import CenteredLoadingOverlay from "@Components/generic/centered-loading-overlay";
import { Events } from "@Context/common/general/context";
import { formatName } from "@Helpers/formatters";
import { Api } from "@Shared/schemas";
import { RoleRecord } from "@Shared/schemas/accounts/accountSchemas";
import { InviteResponse } from "@Shared/schemas/accounts/inviteSchemas";
import { useApi } from "@hooks";
import useI18n from "@i18n";
import { Box, Dialog, Stack, Typography, useTheme } from "@mui/material";
import React from 'react';

interface InviteOffersDialogVRProps {
    open: boolean;
    invites: InviteResponse[];
    onClose?: () => void;
}

const InviteOffersDialogVR = ({
    open,
    invites,
    onClose = () => {},
}: InviteOffersDialogVRProps) => {
    const { i18n } = useI18n();
    const theme = useTheme();

    const { call: getRoles, data: rolesData = [] } = useApi({
        api: Api.Account.GetRoles,
        request: {},
    });

    const { call: consumeInvite, loading: consumeInviteLoading } = useApi({
        api: Api.Invites.ConsumeInvite,
        request: {} as any,
        onSuccess: () => {
            Events.account.refreshCurrentAccount.emit({
                onSuccess: () => {
                    onClose();
                }
            });
            onClose();
        }
    });

    React.useEffect(() => {
        getRoles();
    }, []);

    const handleAcceptInvite = async (inviteId: number) => {
        await consumeInvite({
            invite_id: inviteId,
            action: 'ACCEPT'
        });
    };

    const handleDeclineInvite = async (inviteId: number) => {
        await consumeInvite({
            invite_id: inviteId,
            action: 'DECLINE'
        });
    };

    const getRoleName = (roleId: number | null) => {
        if (!roleId) return '';
        const role = rolesData?.find((r: RoleRecord) => r.id === roleId);
        return role ? role.name : '';
    };

    return (
        <Dialog 
            open={open} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: 'base.v100',
                    borderRadius: theme.spacing(3)
                }
            }}
        >
            {consumeInviteLoading && <CenteredLoadingOverlay loading={consumeInviteLoading} />}
            <Box sx={{ p: 4 }}>
                <Typography variant="h4" color="dsBase.v200" gutterBottom>
                    {i18n("settings.members.pending-invites.title")}
                </Typography>
                
                <Typography variant="body1" color="dsNeutral.v300" sx={{ mb: 3 }}>
                    {i18n("settings.members.pending-invites.description")}
                </Typography>

                <Stack spacing={2}>
                    {invites.map((invite) => (
                        <Box
                            key={invite.id}
                            sx={(theme) => ({
                                ...surfaceV100Background(theme),
                                p: 3,
                                borderRadius: theme.spacing(2)
                            })}
                        >
                            <Stack spacing={2}>
                                <Stack spacing={0.5}>
                                    <Typography variant="text1xl600" color="dsBase.v200">
                                        {invite.account?.name}
                                    </Typography>
                                    <Stack spacing={0.5}>
                                        <Typography variant="textLg500" color="dsNeutral.v300">
                                            {i18n(`roles.${getRoleName(invite.role_id)}`)}
                                        </Typography>
                                        {invite.sender && (
                                            <Typography variant="textS500" color="dsNeutral.v300">
                                                {i18n("settings.members.invited-by", {
                                                    name: formatName(invite.sender.first_name, invite.sender.last_name)
                                                })}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                                
                                <Stack direction="row" spacing={2}>
                                    <BybButtonVR
                                        color="primary"
                                        onClick={() => handleAcceptInvite(invite.id)}
                                        sx={{ flex: 1 }}
                                    >
                                        {i18n("settings.members.pending-invites.accept")}
                                    </BybButtonVR>
                                    <BybButtonVR
                                        color="tertiary"
                                        onClick={() => handleDeclineInvite(invite.id)}
                                        sx={{ flex: 1 }}
                                    >
                                        {i18n("settings.members.pending-invites.decline")}
                                    </BybButtonVR>
                                </Stack>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </Box>
        </Dialog>
    );
};

export default InviteOffersDialogVR; 